"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AuthToken, User } from "@/lib/types";

function parseJwtUser(token: string): User | null {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const data = JSON.parse(decoded) as Record<string, unknown>;
    if (!data.user_id || !data.sub) return null;
    return {
      user_id: data.user_id as number,
      username: data.sub as string,
      email: (data.email as string) ?? "",
      is_admin: (data.is_admin as boolean) ?? false,
      is_active: true,
      created_at: "",
    };
  } catch {
    return null;
  }
}

interface AuthContextValue {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoadingUser: boolean;
  setSessionFromToken: (auth: AuthToken) => Promise<void>;
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_STORAGE_KEY = "fotmob.auth.session";
const EXPIRY_SKEW_MS = 30_000;

type StoredSession = {
  token: string;
  expiresAt: number;
};

function readStoredSession(): StoredSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed.token || typeof parsed.expiresAt !== "number") {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }

    return {
      token: parsed.token,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

function clearStoredSession(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  useEffect(() => {
    let active = true;
    const hydrate = async () => {
      const stored = readStoredSession();
      if (!stored) {
        if (active) {
          setIsLoadingUser(false);
        }
        return;
      }

      if (Date.now() + EXPIRY_SKEW_MS >= stored.expiresAt) {
        clearStoredSession();
        if (active) {
          setToken(null);
          setUser(null);
          setIsLoadingUser(false);
        }
        return;
      }

      const user = parseJwtUser(stored.token);
      if (!active) return;

      if (!user) {
        clearStoredSession();
        setToken(null);
        setUser(null);
      } else {
        setToken(stored.token);
        setUser(user);
      }
      setIsLoadingUser(false);
    };

    void hydrate();

    return () => {
      active = false;
    };
  }, []);

  const setSessionFromToken = async (auth: AuthToken) => {
    const user = parseJwtUser(auth.access_token);
    if (!user) throw new Error("Invalid token received from server");

    const expiresAt = Date.now() + auth.expires_in * 1000;
    writeStoredSession({ token: auth.access_token, expiresAt });
    setToken(auth.access_token);
    setUser(user);
  };

  const clearSession = () => {
    setToken(null);
    setUser(null);
    clearStoredSession();
  };

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token),
      isLoadingUser,
      setSessionFromToken,
      clearSession,
    }),
    [token, user, isLoadingUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
